// SolveAbsoluteLinearTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

/* === UTIL === */
function gcd(a, b) { while (b !== 0) [a, b] = [b, a % b]; return Math.abs(a); }
function formatFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;
  if (frac === 0) return `${sign}${intPart}`;
  for (let d = 1; d <= 10; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g, den = d / g;
      return intPart === 0 ? `${sign}\\dfrac{${num}}{${den}}` : `${sign}${intPart}\\dfrac{${num}}{${den}}`;
    }
  }
  return `${sign}${abs.toFixed(2)}`;
}

/* === GENERATE TASK === */
export function generateTask(options = {}) {
  let a, b, c, x1, x2;
  const sign = options.sign || (Math.random() < 0.5 ? "+" : "-");

  // a ≠ 0
  if (options.forceUnitXCoefficient) {
    a = 1;
  } else if (options.forceNonUnitXCoefficient) {
    a = Math.floor(Math.random() * 4) + 2;
  } else {
    do { a = Math.floor(Math.random() * 5 + 1) * (Math.random() < 0.5 ? -1 : 1); } while (a === 0);
  }

  // b dowolne, c > 0
  b = Math.floor(Math.random() * 11 + 1) * (Math.random() < 0.5 ? -1 : 1);
  c = Math.floor(Math.random() * 15 + 1);
  const outsideCoefficient = options.forceOutsideCoefficient
    ? Math.floor(Math.random() * 4) + 2
    : 1;
  const rightSide = outsideCoefficient * c;

  // Spójność zapisu i liczenia: używamy |b| ze znakiem z 'sign'
  const B = Math.abs(b);
  const signedB = sign === "+" ? +B : -B;

  // Rozwiązania
  x1 = (c - signedB) / a;
  x2 = (-c - signedB) / a;

  // Do wyjaśnień — wartości po zsumowaniu/odjęciu
  const sum1 = sign === "+" ? (c - B) : (c + B);     // c ±(-) B
  const sum2 = sign === "+" ? (-c - B) : (-c + B);   // -c ±(-) B

  // LaTeX równania
  const ax = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  const bPart = B === 0 ? "" : sign === "+" ? `+ ${B}` : `- ${B}`;
  const absLatex = `\\left|${ax} ${bPart}\\right|`;
  const latex = `${outsideCoefficient === 1 ? "" : outsideCoefficient}${absLatex} = ${rightSide}`;

  return {
    type: "SolveAbsoluteLinearTask",
    data: {
      a,
      b,
      c,
      sign,
      x1,
      x2,
      latex,
      absLatex,
      outsideCoefficient,
      rightSide,
      sum1,
      sum2,
    }
  };
}

/* === RENDER TASK === */
export function renderTask({ data }) {
  return <p>Rozwiąż równanie: <InlineMath math={data.latex} /></p>;
}

export function renderInput(_, userInput, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span><InlineMath math="x_1 =" /></span>
        <FractionInput
          value={userInput?.x1 || ""}
          onChange={val => onChange({ ...userInput, x1: val })}
          disabled={disabled}
          label="x_1"
        />
      </div>
      <div className="flex items-center gap-2">
        <span><InlineMath math="x_2 =" /></span>
        <FractionInput
          value={userInput?.x2 || ""}
          onChange={val => onChange({ ...userInput, x2: val })}
          disabled={disabled}
          label="x_2"
        />
      </div>
    </div>
  );
}

/* === CHECK ANSWER === */
export function checkAnswer({ data }, userInput) {
  const { x1, x2 } = data;
  const u1 = userInput?.x1?.parsed;
  const u2 = userInput?.x2?.parsed;
  const correct =
    (Math.abs(u1 - x1) < 0.01 && Math.abs(u2 - x2) < 0.01) ||
    (Math.abs(u1 - x2) < 0.01 && Math.abs(u2 - x1) < 0.01);
  return { correct, expected: [x1, x2] };
}

// Krótka odpowiedź: większy odstęp między x1 i x2
export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem" }}>
      <div style={{ marginBottom: "0.6rem" }}>
        <InlineMath math={`x_1 = ${formatFractionLatex(data.x1)}`} />
      </div>
      <div>
        <InlineMath math={`x_2 = ${formatFractionLatex(data.x2)}`} />
      </div>
    </div>
  );
}

// Podgląd odpowiedzi użytkownika: taki sam odstęp
export function renderUserAnswer(userInput = {}) {
  const raw1 = userInput?.x1?.raw || "";
  const raw2 = userInput?.x2?.raw || "";

  const prev1 = raw1 ? formatLatexPreview(raw1, "x_1") : "";
  const prev2 = raw2 ? formatLatexPreview(raw2, "x_2") : "";

  // jeśli nic nie wpisano w oba pola
  if (!prev1 && !prev2) {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ marginTop: "1rem", textAlign: "left" }}>
      {prev1 ? (
        <div style={{ marginBottom: "0.6rem" }}>
          <InlineMath math={prev1} />
        </div>
      ) : null}

      {prev2 ? (
        <div>
          <InlineMath math={prev2} />
        </div>
      ) : null}
    </div>
  );
}


export function renderExplanation({ data }) {
  const {
    a,
    b,
    c,
    sign,
    x1,
    x2,
    sum1,
    sum2,
    outsideCoefficient = 1,
    rightSide = c,
  } = data;

  const ax = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  const B  = Math.abs(b);
  const pm = sign === "+" ? "+ " : "- ";
  const mp = sign === "+" ? "- " : "+ ";

  // Krok 1 — dwa wyśrodkowane wiersze (bez strzałki)
  const k1a = `\\left|${ax} ${pm}${B}\\right| = ${c}`;
  const k1b = `${ax} ${pm}${B} = ${c}\\quad \\text{lub}\\quad ${ax} ${pm}${B} = -${c}`;

  // Krok 2 — rachunki (każda linia osobno, bez 'aligned')
  // Pierwsze równanie
  const s1 = `${ax} ${pm}${B} = ${c}`;
  const s2 = `${ax} = ${c} ${mp}${B}`;
  const s3 = `${ax} = ${sum1}`;
  const s4 = `x_1 = ${formatFractionLatex(x1)}`;

  // Drugie równanie
  const t1 = `${ax} ${pm}${B} = -${c}`;
  const t2 = `${ax} = -${c} ${mp}${B}`;
  const t3 = `${ax} = ${sum2}`;
  const t4 = `x_2 = ${formatFractionLatex(x2)}`;

  return (
    <div className="space-y-4">
      {outsideCoefficient !== 1 ? (
        <>
          <p>
            <strong>Krok 1:</strong> Dzielimy obie strony równania przez{" "}
            <InlineMath math={`${outsideCoefficient}`} />.
          </p>
          <div style={{ textAlign: "center" }}>
            <BlockMath
              math={`${outsideCoefficient}\\left|${ax} ${pm}${B}\\right|=${rightSide}`}
            />
            <BlockMath math={`\\left|${ax} ${pm}${B}\\right|=${c}`} />
          </div>
        </>
      ) : null}

      <p>
        <strong>Krok {outsideCoefficient !== 1 ? "2" : "1"}:</strong>{" "}
        Rozpisujemy wartość bezwzględną na dwa równania.
      </p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={k1a} />
        <BlockMath math={k1b} />
      </div>

      <p>
        <strong>Krok {outsideCoefficient !== 1 ? "3" : "2"}:</strong>{" "}
        Rozwiązujemy oba równania.
      </p>

      <p><em>Pierwsze równanie:</em></p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={s1} />
        <BlockMath math={s2} />
        <BlockMath math={s3} />
        <BlockMath math={s4} />
      </div>

      <p><em>Drugie równanie:</em></p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={t1} />
        <BlockMath math={t2} />
        <BlockMath math={t3} />
        <BlockMath math={t4} />
      </div>

      <p><strong>Odpowiedź końcowa:</strong></p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`x_1 = ${formatFractionLatex(x1)},\\quad x_2 = ${formatFractionLatex(x2)}`} />
      </div>
    </div>
  );
}

